import type {
  FriendshipStatus,
  FriendEntry,
  KorvexGame,
  KorvexGameDetail,
  PublicUser,
  SessionUser,
} from '../types';

const TOKEN_KEY = 'korvex_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  }
  return data;
}

export const api = {
  register(username: string, password: string) {
    return request<{ user: SessionUser; token: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  login(username: string, password: string) {
    return request<{ user: SessionUser; token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  logout() {
    setStoredToken(null);
    return request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
  },
  me() {
    return request<{ user: SessionUser }>('/api/auth/me');
  },
  updateSettings(data: { darkMode?: boolean; bio?: string }) {
    return request<{ user: SessionUser }>('/api/auth/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  getUser(username: string) {
    return request<{ user: PublicUser; friendshipStatus: FriendshipStatus; canAdmin: boolean }>(
      `/api/users/${encodeURIComponent(username)}`,
    );
  },
  searchUsers(q: string) {
    return request<{ users: PublicUser[] }>(`/api/users/search?q=${encodeURIComponent(q)}`);
  },
  getFriends() {
    return request<{ friends: FriendEntry[] }>('/api/friends');
  },
  sendFriendRequest(username: string) {
    return request<{ ok: boolean; status: string }>(`/api/friends/request/${encodeURIComponent(username)}`, {
      method: 'POST',
    });
  },
  acceptFriendRequest(requestId: string) {
    return request<{ ok: boolean }>(`/api/friends/requests/${requestId}/accept`, { method: 'POST' });
  },
  declineFriendRequest(requestId: string) {
    return request<{ ok: boolean }>(`/api/friends/requests/${requestId}`, { method: 'DELETE' });
  },
  removeFriend(username: string) {
    return request<{ ok: boolean }>(`/api/friends/${encodeURIComponent(username)}`, { method: 'DELETE' });
  },
  adminDeleteUser(username: string) {
    return request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
  },
  adminBanUser(username: string, days: number, reason: string) {
    return request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/ban`, {
      method: 'POST',
      body: JSON.stringify({ days, reason }),
    });
  },
  adminUnbanUser(username: string) {
    return request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(username)}/unban`, {
      method: 'POST',
    });
  },
  createGame(name?: string) {
    return request<{ game: KorvexGameDetail }>('/api/games', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },
  getMyGames() {
    return request<{ games: KorvexGame[] }>('/api/games/mine');
  },
  getPlayableGames() {
    return request<{ games: KorvexGame[] }>('/api/games/playable');
  },
  getGame(id: string) {
    return request<{ game: KorvexGameDetail }>(`/api/games/${id}`);
  },
  updateGame(id: string, data: Partial<{ name: string; description: string; isPrivate: boolean; sceneData: unknown }>) {
    return request<{ game: KorvexGameDetail }>(`/api/games/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  publishGame(
    id: string,
    data: { name?: string; description?: string; isPrivate?: boolean; sceneData?: unknown },
  ) {
    return request<{ game: KorvexGameDetail }>(`/api/games/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export function saveSession(token: string) {
  setStoredToken(token);
}

declare global {
  interface Window {
    korvexPlayGame?: (gameId: string) => void;
    korvexOpenStudioGame?: (gameId: string) => void;
    korvexAuthToken?: () => string | null;
  }
}
